import { Component, OnDestroy, OnInit } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import moment from 'moment';
import { Subject, throwError, zip } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TimeAllocationService } from './time-allocation.service';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DemographicsService } from '../demographics/demographics.service';
import { Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModulesModule } from '../shared/shared-modules.module';
import { SharedComponentsModule } from '../shared/shared-components.module';
import { SharedPipesModule } from '../shared/shared-pipes.module';
import { TimeAllocationLocation, TimeAllocationWorkedService, TimeAllocationRole } from '../shared/time-allocation.interface';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
    dbInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MM/DD/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-time-allocation',
  templateUrl: './time-allocation.component.html',
  styleUrls: ['./time-allocation.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
  imports: [
    SharedModulesModule,
    SharedComponentsModule,
    SharedPipesModule,
    RouterLink,
  ],
})
export class TimeAllocationComponent implements OnInit, OnDestroy {

  private unsubscriber: Subject<void> = new Subject<void>();
  allocationForm: UntypedFormGroup;
  loading = false;
  user;
  message;

  allocationID = 0;
  allocationLocations: TimeAllocationLocation[];
  allocationServices: TimeAllocationWorkedService[];
  allocationRoles: TimeAllocationRole[];
  showRoles = false;

  constructor(public fb: UntypedFormBuilder,
    public timeAllocationService: TimeAllocationService,
    public demoService: DemographicsService,
    private router: Router,
    private snackbar: MatSnackBar) { }

  ngOnInit(): void {
      this.loading = true;
      this.allocationID = history.state.allocationID;
      this.timeAllocationService.getTimeAllocationLocations().subscribe(locations => {
        this.allocationLocations = locations;
      });
      this.timeAllocationService.getTimeAllocationServices().subscribe(services => {  
        this.allocationServices = services;
      });
      this.timeAllocationService.getTimeAllocationRoles().subscribe(roles => {  
        this.allocationRoles = roles;
      });
      this.demoService.getUserData().pipe(
        takeUntil(this.unsubscriber),
        catchError(err => {
          console.log('ERROR: ', err);
          return throwError(err);
        })
      ).subscribe(user => {
        this.user = user;
        zip(
          this.demoService.getProfile(user.employee_id),
        ).pipe(
          takeUntil(this.unsubscriber),
          catchError(err => { return throwError(err); }),
          finalize(() => this.loading = false)
        ).subscribe(() => {
          this.setupAllocationForm();
        });
      }, (e) => {
        console.log('Error: ', e);
      });
    }

  setupAllocationForm() {
    this.allocationForm = this.fb.group({
      location: ['', Validators.required],
      serviceDate: ['', Validators.required],
      service:  ['', Validators.required],
      role:  [null],
      hoursWorked: ['', Validators.required],
      hoursTraveled: [null],
      note: [null],
    });

    if (this.allocationID) {
      this.timeAllocationService.getTimeAllocation(this.allocationID).pipe(
      takeUntil(this.unsubscriber),
      catchError((err => {
          console.log('ERROR: ', err);
          return throwError(err);
        })
      )).subscribe(result => {
        this.allocationForm.patchValue({
          location: result.locationID,
          serviceDate: moment(result.serviceDate, 'YYYY-MM-DD'),
          service: this.allocationServices.find(svc => svc.allocationServiceID === result.allocationServiceID),
          role: result.allocationRoleID,
          hoursWorked: result.hoursWorked,
          hoursTraveled: result.hoursTraveled,
          note: result.allocationNote,
      })
      this.serviceChanged({value: this.allocationForm.value.service});
      });
    }
  }

  serviceChanged($event) {
    const selectedService = $event.value.serviceDescription;
    if (selectedService === 'In-Facility Role') {
      this.showRoles = true;
      this.allocationForm.controls['role'].setValidators([Validators.required]);
    }
    else {
      this.showRoles = false;
      this.allocationForm.get('role').reset();
      this.allocationForm.controls['role'].clearValidators();
      this.allocationForm.controls['role'].updateValueAndValidity();
    }
  }

  onSubmit(form: UntypedFormGroup): void {
    console.log('submitting', form.value);
    this.loading = true;

    const data = 
      {
        allocationID: this.allocationID,
        employeeID: this.user.employee_id,
        locationID: form.value.location,
        serviceDate: form.value.serviceDate,
        hoursWorked: form.value.hoursWorked,
        hoursTraveled: form.value.hoursTraveled,
        allocationNote: form.value.note,
        allocationServiceID: form.value.service.allocationServiceID,
        allocationRoleID: form.value.role,
        delete: false,
      };

      // set message
      this.message = 'Saving...';
      console.log('Time Allocation Data: ', data);

      this.timeAllocationService.save(data).pipe(
        takeUntil(this.unsubscriber),
        finalize(() => { this.loading = false; this.message = null; }),
        catchError(err => throwError(err))
      ).subscribe(result => {
        let snackbarMessage = '';
        console.log('Result: ', result);
        if (result) {
          if (JSON.stringify(result).includes('ERROR')) {
            snackbarMessage = result.replace('ERROR: ', '');
          } else {
            snackbarMessage = 'Time Allocation Success';
          }
        } else {
          snackbarMessage = 'Time Allocation Failure';
        }
        this.snackbar.open(snackbarMessage, 'Dismiss');
        this.router.navigate(['demographics']);
      });
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}